import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { ExternalLink } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { lighten } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type KnowledgeBaseCitation } from '../../../../../util/knowledgeBaseCitation';
import { CsvAttachmentViewer } from './CsvAttachmentViewer';
import { useResolveKnowledgeBaseCsv } from './useResolveKnowledgeBaseCsv';

const messages = defineMessages({
  loadError: {
    id: 'phc-web-toolkit.subjectViewer.attachments.knowledgeBaseCsvInline.loadError',
    defaultMessage: 'Could not load CSV from knowledge base.',
  },
  download: {
    id: 'phc-web-toolkit.subjectViewer.attachments.knowledgeBaseCsvInline.download',
    defaultMessage: 'Download CSV',
  },
});

const useStyles = makeStyles(theme => ({
  expansionPanel: {
    borderColor: theme.palette.divider,
    border: '1px solid',
    borderRadius: theme.pxToRem(4),
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    margin: theme.spacing(1.5, 0),
    '& div': {
      boxShadow: 'none',
      background: lighten(theme.palette.graphite[900], 0.9),
    },
    '& button': {
      backgroundColor: 'transparent',
    },
    '@media print': {
      '&': {
        breakInside: 'avoid',
      },
    },
  },
  expansionPanelContent: {
    padding: 0,
    height: 600,
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    backgroundColor: 'white',
  },
  noMaxHeightContent: {
    maxHeight: 'unset !important',
  },
  externalLink: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  loader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 240,
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(2),
  },
}));

export type KnowledgeBaseCsvInlineProps = {
  citation: KnowledgeBaseCitation;
  /** When true, expand the panel by default (used in print/expanded modes). */
  defaultExpanded?: boolean;
  isPrint?: boolean;
};

/**
 * Renders a knowledge-base CSV citation inline as an expansion panel containing
 * a sortable table preview. Used in expanded executive-summary view modes so
 * the report reads top-to-bottom without forcing the user to download anything.
 */
export const KnowledgeBaseCsvInline: React.FC<KnowledgeBaseCsvInlineProps> = ({
  citation,
  defaultExpanded = true,
  isPrint = false,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const state = useResolveKnowledgeBaseCsv(citation);
  const [isOpen, setIsOpen] = React.useState(defaultExpanded);

  const renderContent = () => {
    if (state.status === 'loading' || state.status === 'idle') {
      return (
        <Box className={classes.loader}>
          <DotLoader size={1} />
        </Box>
      );
    }
    if (state.status === 'error' || state.status === 'not-csv') {
      return (
        <Box className={classes.error}>
          <Text>{formatMessage(messages.loadError)}</Text>
        </Box>
      );
    }
    return (
      <CsvAttachmentViewer
        url={state.resolved.url}
        downloadUrl={state.resolved.downloadUrl}
        fileName={state.resolved.fileName}
        hideHeader
        data-testid="kb-csv-inline-viewer"
      />
    );
  };

  const downloadHref =
    state.status === 'success' ? state.resolved.downloadUrl : undefined;

  return (
    <ExpansionPanel
      title={citation.displayName}
      isOpen={isOpen || isPrint}
      onToggle={setIsOpen}
      className={classes.expansionPanel}
      innerContentClassName={classes.expansionPanelContent}
      contentClassName={isPrint ? classes.noMaxHeightContent : ''}
      secondaryHeader={
        downloadHref ? (
          <Tooltip title={formatMessage(messages.download)}>
            <IconButton
              aria-label={formatMessage(messages.download)}
              data-testid="kb-csv-inline-download"
              className={classes.externalLink}
              icon={ExternalLink}
              size={0}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(downloadHref, '_blank', 'noopener noreferrer');
              }}
            />
          </Tooltip>
        ) : null
      }
    >
      {renderContent()}
    </ExpansionPanel>
  );
};
